import genericStyles from "../../Styles/genericStyles.module.css";
import Config from "../../Utils/Config";
import { TablesIds } from "../../Utils/Constants";
import { EComponentTypes, ETableEditAction } from "../../Utils/Enums";
import { Filter } from "../../Utils/Types";
import { endpoints, localUrlEnum } from "../../Utils/UrlEnum";
import { Vocabulary } from "../../Utils/Vocabulary";
import MUIDataTableCustomStyle from "../MUIDataTableCustomStyle";
import * as yup from "yup";
import Filters from "../Widgets/Filters";
import {
  ClientModel,
  OrderModel,
  ProductModel,
  UserModel,
  WorkPointModel,
} from "../../Utils/Models";
import {
  fetchProducts,
  formatDate,
  getFullName,
  OrderStatus,
} from "../../Utils/Utils";
import { getFromCacheOrFetch } from "../../Utils/getFromCacheOrFetch";
import { useEffect, useState, useMemo } from "react";
import useCustomMemo from "../../Hooks/useCustomMemo";
import { useNavigate } from "react-router-dom";

export default function ScheduledExams() {
  const [products, setProducts] = useState<ProductModel[]>([]);
  const memo = useCustomMemo();
  const workPoints: WorkPointModel[] = memo?.storage?.workPoints;
  const navigate = useNavigate();

  /**
   * UseEffect hook to retrieve all products from cache or API when the component mounts.
   * Filters to show only products with isCertificate: false.
   */
  useEffect(() => {
    let mounted = true;

    getFromCacheOrFetch<any[]>({
      cacheKey: "products",
      storage: memo.storage,
      setStorage: memo.setStorageData,
      fetcher: fetchProducts(),
      isValid: (data) => Array.isArray(data) && data.length > 0,
    }).then((data) => {
      if (mounted) {
        setProducts(data?.filter((p) => p.isCertificate === false) || []);
      }
    });

    return () => {
      mounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   *  Defines the columns for the table.
   */
  const columns = [
    { name: "orderId", label: " ", options: { display: false } },
    {
      name: "orderDetails",
      label: Vocabulary.id,
      options: {
        customBodyRender: (order: OrderModel) => {
          return order?.orderNumber;
        },
        exportValue: (order: OrderModel) => order?.orderNumber || "",
      },
    },
    {
      name: "workPoint",
      label: Vocabulary.workPoint,
      options: {
        sortField: "workPoint.name",
        customBodyRender: (workPoint: WorkPointModel) => {
          return workPoint?.name;
        },
        exportValue: (workPoint: WorkPointModel) => workPoint?.name || "",
      },
    },
    {
      name: "createdAt",
      label: Vocabulary.programedDate,
      options: {
        customBodyRender: (date: Date) => {
          return formatDate(date);
        },
        exportValue: (date: Date) => date ? formatDate(date) : "",
      },
    },
    {
      name: "clientDetails",
      label: Vocabulary.cursant,
      options: {
        sortField: "clientDetails.firstName",
        customBodyRender: (client: ClientModel) => {
          return getFullName(client);
        },
        exportValue: (client: ClientModel) => getFullName(client),
      },
    },
    {
      name: "orderDetails",
      label: Vocabulary.category,
      options: {
        sortField: "orderDetails.product.name",
        customBodyRender: (order: OrderModel) => {
          return order?.product?.name;
        },
        exportValue: (order: OrderModel) => order?.product?.name || "",
      },
    },
    {
      name: "examDate",
      label: Vocabulary.examDate,
      options: {
        customBodyRender: (date: Date) => {
          return formatDate(date);
        },
        exportValue: (date: Date) => date ? formatDate(date) : "",
      },
    },
    {
      name: "instructorUserDetails",
      label: Vocabulary.instructor,
      options: {
        sortField: "instructorUserDetails.firstName",
        customBodyRender: (instructor: UserModel) => {
          return getFullName(instructor);
        },
        exportValue: (instructor: UserModel) => getFullName(instructor),
      },
    },
    {
      name: "type",
      label: Vocabulary.examType,
    },
  ];

  /**
   * Defines the filters for the ScheduledExams table.
   */
  const filters = useMemo<Filter[]>(() => [
    {
      property: "start",
      label: Vocabulary.startDate,
      type: EComponentTypes.DATE_PICKER,
      validation: yup.string().required(Vocabulary.requiredField),
      defaultValue: Config.defaultStartDate,
    },
    {
      property: "end",
      label: Vocabulary.endDate,
      type: EComponentTypes.DATE_PICKER,
      validation: yup.string().required(Vocabulary.requiredField),
      defaultValue: Config.defaultEndDate,
    },
    {
      property: "examType",
      autocompleteFilterProperty: "name",
      label: Vocabulary.examType,
      type: EComponentTypes.AUTOCOMPLETE,
      validation: null,
      defaultValue: null,
      options: [
        { name: OrderStatus.theoretical },
        { name: OrderStatus.practical },
      ],
    },
    {
      property: "productId",
      autocompleteFilterProperty: "_id",
      label: Vocabulary.category,
      type: EComponentTypes.AUTOCOMPLETE,
      validation: null,
      defaultValue: null,
      options: products,
    },
    {
      property: "workPointId",
      autocompleteFilterProperty: "_id",
      label: Vocabulary.workPoint,
      type: EComponentTypes.AUTOCOMPLETE,
      validation: null,
      defaultValue: null,
      options: workPoints,
    },
  ], [products, workPoints]);

  /**
   * Handles row click to navigate to order page
   * @param rowData - The data of the clicked row
   */
  const handleRowClick = (rowData: any) => {
    const orderId = rowData.orderId;
    if (orderId) {
      navigate(`${localUrlEnum.order}/${orderId}`);
    }
  };

  /**
   * Renders the ScheduledExams component.
   */
  return (
    <div className={genericStyles.mainContainer}>
      <Filters filters={filters} filterWidth={2} defaultOpen />
      <MUIDataTableCustomStyle
        buttons={[]}
        onEditAction={ETableEditAction.NONE}
        title={Vocabulary.scheduledExams}
        id={TablesIds.examsTable}
        hideAddButton={true}
        columns={columns}
        CRUDEndPoint={endpoints.exams.main}
        hideEditButton={true}
        hideDeleteButton={true}
        onRowClick={handleRowClick}
        showPrintButton
        showDownloadButton
      />
    </div>
  );
}
